import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight, FileText, Globe, Users,
  Inbox, CreditCard, Mail, LayoutDashboard, Settings, FileSpreadsheet,
  CalendarClock, Sparkles
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/strategy-sessions", label: "Strategy Sessions", icon: CalendarClock },
  { to: "/admin/briefs", label: "Luminous Briefs", icon: Sparkles },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox },
  { to: "/admin/industries", label: "Industries", icon: Globe },
  { to: "/admin/articles", label: "Articles", icon: FileText },
  { to: "/admin/reports", label: "Reports", icon: FileSpreadsheet },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="min-h-screen terminal-bg flex items-center justify-center text-[var(--terminal-muted)] font-mono">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen terminal-bg flex" data-testid="admin-layout">
      <aside className="w-[240px] border-r border-[var(--terminal-border)] flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-5 border-b border-[var(--terminal-border)]">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block w-6 h-6 border border-[var(--terminal-brass)] flex items-center justify-center font-display font-black text-sm text-[var(--terminal-brass)]">L</span>
            <span className="font-display font-black text-lg text-[var(--terminal-text)]">LUMINARY</span>
          </Link>
          <div className="mt-1 text-[9px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">Admin Console</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                "flex items-center gap-3 px-5 py-2.5 text-[12px] font-mono uppercase tracking-[0.18em] transition-colors " +
                (isActive ? "bg-[var(--terminal-border)]/50 text-[var(--terminal-brass)] border-l-2 border-[var(--terminal-brass)]" : "text-[var(--terminal-muted)] hover:text-[var(--terminal-text)]")
              }
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
            >
              <n.icon size={14} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-[var(--terminal-border)] text-[11px] font-mono text-[var(--terminal-muted)]">
          <div>{user.email}</div>
          <button onClick={logout} className="mt-2 text-[var(--terminal-brass)] hover:text-[var(--signal)]" data-testid="admin-logout">Sign out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-auto"><Outlet /></main>
    </div>
  );
}

export function AdminOverview() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setM(r.data)); }, []);
  const cards = m ? [
    { l: "Strategy Sessions", v: m.strategy_sessions ?? m.inbox_new ?? "—", to: "/admin/strategy-sessions" },
    { l: "Luminous Briefs", v: m.luminous_briefs ?? "—", to: "/admin/briefs" },
    { l: "Articles", v: m.articles, to: "/admin/articles" },
    { l: "Reports", v: m.reports, to: "/admin/reports" },
    { l: "Industries", v: m.industries, to: "/admin/industries" },
    { l: "Users", v: m.users, to: "/admin/users" },
    { l: "Members (paid)", v: m.members, to: "/admin/users" },
    { l: "Newsletter Subs", v: m.newsletter_subs, to: "/admin/newsletter" },
    { l: "Inbox (new)", v: m.inbox_new, to: "/admin/inbox" },
    { l: "Paid Payments", v: m.payments_paid, to: "/admin/payments" },
  ] : [];
  return (
    <div className="p-8" data-testid="admin-overview">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">Control Center</div>
      <h1 className="font-display font-black text-4xl text-[var(--terminal-text)] mt-2">Overview</h1>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-px bg-[var(--terminal-border)] border border-[var(--terminal-border)]" data-testid="admin-metrics-row">
        {cards.map((c) => (
          <Link to={c.to} key={c.l} className="bg-[var(--deep-space)] p-5 hover:bg-[var(--terminal-border)]/30 transition-colors block">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-muted)]">{c.l}</div>
            <div className="font-display font-black text-3xl text-[var(--terminal-text)] mt-2">{c.v ?? "—"}</div>
            <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">Manage →</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function AdminTable({ collection, label, columns, onPublish }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/admin/${collection}`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, [collection]);

  const remove = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    await api.delete(`/admin/${collection}/${slug}`);
    load();
  };
  const publish = async (slug) => {
    setBusy(slug);
    try { await api.post(`/admin/${collection}/${slug}/publish`); load(); } catch {}
    setBusy("");
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS</div>
          <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">{label}</h1>
        </div>
        <Link to={`/admin/${collection}/new`} className="bg-[var(--terminal-brass)] text-[var(--deep-space)] px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] hover:bg-[var(--signal)] transition-colors flex items-center gap-2" data-testid={`admin-new-${collection}`}>
          New {label.replace(/s$/, "")} <ArrowRight size={12} />
        </Link>
      </div>
      <div className="terminal-card overflow-x-auto" data-testid="cms-data-table">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--terminal-border)]">
              {columns.map((c) => <th key={c.k} className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--terminal-brass)]">{c.label}</th>)}
              <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--terminal-brass)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.slug} className="border-b border-[var(--terminal-border)]/50 hover:bg-[var(--terminal-border)]/20">
                {columns.map((c) => <td key={c.k} className="px-4 py-3 text-[var(--terminal-text)]">{c.render ? c.render(it) : (it[c.k] ?? "—")}</td>)}
                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                  <Link to={`/admin/${collection}/${it.slug}/edit`} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--terminal-brass)] hover:text-[var(--signal)]">Edit</Link>
                  {onPublish && it.status !== "published" && (
                    <button onClick={() => publish(it.slug)} disabled={busy === it.slug} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--signal)] hover:text-[var(--terminal-text)]">
                      {busy === it.slug ? "..." : "Publish"}
                    </button>
                  )}
                  <button onClick={() => remove(it.slug)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-red-400 hover:text-red-300">Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-[var(--terminal-muted)] font-mono text-sm">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminArticles() {
  return <AdminTable
    collection="articles"
    label="Articles"
    onPublish
    columns={[
      { k: "title", label: "Title" },
      { k: "type", label: "Type" },
      { k: "status", label: "Status" },
      { k: "industry_slugs", label: "Industries", render: (it) => (it.industry_slugs || []).join(", ") },
    ]} />;
}
export function AdminReports() {
  return <AdminTable collection="reports" label="Reports" onPublish columns={[
    { k: "title", label: "Title" }, { k: "price_usd", label: "Price USD" }, { k: "status", label: "Status" },
  ]} />;
}
export function AdminIndustries() {
  return <AdminTable collection="industries" label="Industries" columns={[
    { k: "name", label: "Name" }, { k: "market_size_usd_b", label: "Market $B" }, { k: "cagr_pct", label: "CAGR %" },
  ]} />;
}
export function AdminCompanies() {
  return <AdminTable collection="companies" label="Companies" columns={[
    { k: "name", label: "Name" }, { k: "ticker", label: "Ticker" }, { k: "hq", label: "HQ" },
  ]} />;
}
export function AdminAuthors() {
  return <AdminTable collection="authors" label="Authors" columns={[
    { k: "name", label: "Name" }, { k: "title", label: "Title" },
  ]} />;
}

/** Generic JSON editor for any content type */
export function AdminEditor() {
  const { collection, slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;
  const [item, setItem] = useState(isNew ? { slug: "", title: "", name: "", status: "draft", body_md: "" } : null);
  const [raw, setRaw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/${collection}/by-slug/${slug}`).then((r) => {
        setItem(r.data);
        setRaw(JSON.stringify(r.data, null, 2));
      });
    } else {
      setRaw(JSON.stringify({ slug: "", title: "", status: "draft", body_md: "" }, null, 2));
    }
  }, [collection, slug, isNew]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = JSON.parse(raw);
      if (isNew) await api.post(`/admin/${collection}`, payload);
      else await api.put(`/admin/${collection}/${slug}`, payload);
      navigate(`/admin/${collection}`);
    } catch (e) {
      setErr(e.message || formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="p-8" data-testid="admin-editor">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS · {collection}</div>
      <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">{isNew ? "New" : `Edit · ${slug}`}</h1>
      <p className="mt-3 text-sm text-[var(--terminal-muted)] max-w-2xl">JSON editor. Field schema matches the public model. For articles, set <code className="text-[var(--terminal-brass)]">type</code> (article|briefing|outlook|perspective|case-study|whitepaper|research-note), <code className="text-[var(--terminal-brass)]">status</code> (draft|published), and <code className="text-[var(--terminal-brass)]">industry_slugs</code>.</p>
      <textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        rows={28}
        className="mt-6 w-full terminal-card p-5 font-mono text-[13px] text-[var(--terminal-text)] resize-y"
        data-testid="admin-json-editor"
      />
      {err && <p className="mt-3 text-sm text-red-400 font-mono">{err}</p>}
      <div className="mt-6 flex gap-3">
        <button onClick={save} disabled={busy} className="bg-[var(--terminal-brass)] text-[var(--deep-space)] px-5 py-2 font-mono text-xs uppercase tracking-[0.22em] hover:bg-[var(--signal)] transition-colors" data-testid="admin-save">
          {busy ? "Saving…" : "Save"}
        </button>
        <button onClick={() => navigate(`/admin/${collection}`)} className="px-5 py-2 font-mono text-xs uppercase tracking-[0.22em] text-[var(--terminal-muted)] hover:text-[var(--terminal-text)] border border-[var(--terminal-border)]">Cancel</button>
      </div>
    </div>
  );
}

export function AdminInbox() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/inbox").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.put(`/admin/inbox/${id}/status`, { status }); load(); };
  return (
    <div className="p-8" data-testid="admin-inbox">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS</div>
      <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">Inbox</h1>
      <div className="mt-6 space-y-3">
        {items.map((it) => (
          <div key={it.id} className="terminal-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">{it.type} · {it.status}</div>
                <div className="font-display font-bold text-lg text-[var(--terminal-text)] mt-1">{it.name} <span className="text-[var(--terminal-muted)] font-mono text-xs">· {it.email}</span></div>
                {(it.company || it.role) && <div className="text-xs font-mono text-[var(--terminal-muted)] mt-1">{[it.company, it.role].filter(Boolean).join(" · ")}</div>}
              </div>
              <div className="flex gap-2">
                {["new", "in-progress", "done"].map((s) => (
                  <button key={s} onClick={() => setStatus(it.id, s)} className={"font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border " + (it.status === s ? "bg-[var(--terminal-brass)] text-[var(--deep-space)] border-[var(--terminal-brass)]" : "text-[var(--terminal-muted)] border-[var(--terminal-border)] hover:text-[var(--terminal-text)]")}>{s}</button>
                ))}
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[var(--terminal-text)] whitespace-pre-wrap leading-relaxed">{it.message}</p>
          </div>
        ))}
        {items.length === 0 && <div className="text-[var(--terminal-muted)] font-mono text-sm">Inbox is empty.</div>}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const setPlan = async (id, plan_tier) => { await api.put(`/admin/users/${id}/plan`, { plan_tier }); load(); };
  return (
    <div className="p-8" data-testid="admin-users">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS</div>
      <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">Users</h1>
      <div className="mt-6 terminal-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--terminal-border)]">
              {["Email", "Name", "Role", "Plan", "Joined", "Manage"].map((h) => <th key={h} className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--terminal-brass)]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-[var(--terminal-border)]/50">
                <td className="px-4 py-3 text-[var(--terminal-text)]">{u.email}</td>
                <td className="px-4 py-3 text-[var(--terminal-text)]">{u.name}</td>
                <td className="px-4 py-3 text-[var(--terminal-brass)]">{u.role}</td>
                <td className="px-4 py-3 text-[var(--terminal-text)]">{u.plan_tier}</td>
                <td className="px-4 py-3 text-[var(--terminal-muted)] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 space-x-2">
                  {["guest", "member", "premium", "enterprise"].map((p) => (
                    <button key={p} onClick={() => setPlan(u.id, p)} className={"font-mono text-[10px] uppercase tracking-[0.18em] " + (u.plan_tier === p ? "text-[var(--signal)]" : "text-[var(--terminal-muted)] hover:text-[var(--terminal-text)]")}>{p}</button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminPayments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="p-8" data-testid="admin-payments">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS</div>
      <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">Payments</h1>
      <div className="mt-6 terminal-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--terminal-border)]">
              {["Session", "Kind", "User", "Amount", "Status", "Payment", "Created"].map((h) => <th key={h} className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--terminal-brass)]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.session_id} className="border-b border-[var(--terminal-border)]/50">
                <td className="px-4 py-3 text-[var(--terminal-muted)] font-mono text-xs">{p.session_id?.slice(0, 14)}…</td>
                <td className="px-4 py-3 text-[var(--terminal-text)]">{p.kind}</td>
                <td className="px-4 py-3 text-[var(--terminal-text)]">{p.user_email || "anonymous"}</td>
                <td className="px-4 py-3 text-[var(--terminal-text)]">${p.amount?.toLocaleString()}</td>
                <td className="px-4 py-3 text-[var(--terminal-text)]">{p.status}</td>
                <td className="px-4 py-3 text-[var(--signal)]">{p.payment_status || "—"}</td>
                <td className="px-4 py-3 text-[var(--terminal-muted)] text-xs">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--terminal-muted)] font-mono text-sm">No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminNewsletter() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/newsletter").then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="p-8">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS</div>
      <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">Newsletter Subscribers</h1>
      <div className="mt-6 terminal-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead><tr className="border-b border-[var(--terminal-border)]">{["Email", "Source", "Subscribed"].map((h) => <th key={h} className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--terminal-brass)]">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-[var(--terminal-border)]/50">
                <td className="px-4 py-3 text-[var(--terminal-text)]">{s.email}</td>
                <td className="px-4 py-3 text-[var(--terminal-muted)]">{s.source}</td>
                <td className="px-4 py-3 text-[var(--terminal-muted)] text-xs">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminFeatured() {
  const [data, setData] = useState({ hero_article_slug: "", featured_article_slugs: [], featured_report_slugs: [], featured_podcast_slug: "", featured_company_slugs: [] });
  const [raw, setRaw] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/admin/featured").then((r) => { setData(r.data); setRaw(JSON.stringify(r.data || {}, null, 2)); });
  }, []);
  const save = async () => {
    setMsg("");
    try {
      const payload = JSON.parse(raw);
      await api.put("/admin/featured", payload);
      setMsg("Saved.");
    } catch (e) { setMsg(e.message); }
  };
  return (
    <div className="p-8">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS</div>
      <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">Featured Content</h1>
      <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={18} className="mt-6 w-full terminal-card p-5 font-mono text-[13px] text-[var(--terminal-text)]" />
      <div className="mt-4 flex gap-3 items-center">
        <button onClick={save} className="bg-[var(--terminal-brass)] text-[var(--deep-space)] px-5 py-2 font-mono text-xs uppercase tracking-[0.22em]">Save</button>
        {msg && <span className="font-mono text-xs text-[var(--signal)]">{msg}</span>}
      </div>
    </div>
  );
}

export function AdminSettings() {
  const [raw, setRaw] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/settings").then((r) => setRaw(JSON.stringify(r.data || {}, null, 2))); }, []);
  const save = async () => {
    setMsg("");
    try {
      const payload = JSON.parse(raw);
      await api.put("/admin/settings", payload);
      setMsg("Saved.");
    } catch (e) { setMsg(e.message); }
  };
  return (
    <div className="p-8">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--terminal-brass)]">CMS</div>
      <h1 className="font-display font-black text-3xl text-[var(--terminal-text)] mt-1">Site Settings</h1>
      <p className="mt-2 text-sm text-[var(--terminal-muted)] max-w-2xl">Edit footer contact info, office locations, and social links.</p>
      <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={22} className="mt-6 w-full terminal-card p-5 font-mono text-[13px] text-[var(--terminal-text)]" data-testid="admin-settings-json" />
      <div className="mt-4 flex gap-3 items-center">
        <button onClick={save} className="bg-[var(--terminal-brass)] text-[var(--deep-space)] px-5 py-2 font-mono text-xs uppercase tracking-[0.22em]" data-testid="admin-settings-save">Save</button>
        {msg && <span className="font-mono text-xs text-[var(--signal)]">{msg}</span>}
      </div>
    </div>
  );
}
